/**
 * 약관 컨텐츠 정의 — TermsViewerPage / PublicTermsPage 의 **폴백** 본문.
 *
 * 정상 경로는 BE 약관 도메인(`cm_terms_mst`, 언어별 행)이고 여기는 404·미가용일 때만
 * 쓰인다. 그래서 **한국어만 둔다** — 한국어판이 법적 정본이고, 폴백이 뜨는 화면은
 * `common:terms.originalNotice` 로 "원문 표시"를 함께 안내한다.
 * 제목은 본문과 달리 앱 문구라 3언어가 있다 (TERMS_TITLE_KEY).
 *
 * ⚠ 2026-09-07 — 본문이 통째로 템플릿(픽쿠리) 것이었다: "주식회사 캐노픽", "온라인
 *   가챠/쿠지 커머스 플랫폼", 쿠지 참여 내역·배송지·주문 이력·배송업체 제3자 제공.
 *   peeka 에는 파는 상품도 배송도 없다. peeka 도메인(카드 스캔·시세·컬렉션)으로 다시
 *   썼다. **여전히 placeholder 다 — 출시 전 법무 검토가 필요하다.**
 */

export type TermsType = "service" | "privacy" | "marketing" | "oss";

/**
 * 화면 제목의 i18n 키(card ns). 본문(body)은 BE 약관 도메인이 언어별로 내려주고
 * 여기 fallback 은 한국어 정본뿐이지만, **제목은 앱 문구라 3언어 모두 있다**.
 * 키가 `nav.route.` 로 시작해야 AppBar 가 번역한다 (Layout/ActivityHost 규약).
 */
export const TERMS_TITLE_KEY: Record<TermsType, string> = {
  service: "nav.route.termsService",
  privacy: "nav.route.termsPrivacy",
  marketing: "nav.route.termsMarketing",
  oss: "nav.route.termsOss",
};

export interface TermsContent {
  title: string;
  body: string;
}

export const TERMS_CONTENT: Record<TermsType, TermsContent> = {
  service: {
    title: "이용약관",
    body: `PEEKA 서비스 이용약관 (v1.0)

제 1 조 (목적)
이 약관은 주식회사 덕플렉스(이하 "회사")가 제공하는 PEEKA 서비스(이하 "서비스")의
이용 조건 및 절차, 이용자와 회사의 권리와 의무, 책임 사항 등을 규정함을 목적으로 합니다.

제 2 조 (정의)
1. "서비스"란 회사가 제공하는 트레이딩 카드 인식·시세 조회·컬렉션 관리 서비스를 의미합니다.
2. "이용자"란 본 약관에 따라 회사가 제공하는 서비스를 받는 회원 및 비회원을 말합니다.
3. "시세"란 회사가 외부 거래·판매 데이터를 수집·가공해 제공하는 참고 정보를 의미하며,
   특정 가격에 거래가 성립함을 보장하지 않습니다.

제 3 조 (약관의 효력 및 변경)
1. 본 약관은 회사가 서비스 화면에 게시함으로써 효력이 발생합니다.
2. 회사는 약관을 변경할 수 있으며, 변경 시 30일 이전에 공지합니다.

제 4 조 (서비스 제공)
회사는 카드 이미지 인식, 시세 조회, 컬렉션 관리, 알림 등의 기능을 제공합니다.
서비스는 무료로 제공되며, 유료 기능이 추가될 경우 사전에 공지합니다.

제 5 조 (회원가입)
회원가입은 Google 또는 Apple 계정을 이용한 소셜 로그인으로만 이루어집니다.
소셜 계정마다 별도의 회원으로 취급되며, 서로 다른 계정 간에 데이터는 공유되지 않습니다.

제 6 조 (시세 정보의 성격)
시세는 참고 정보이며 투자 자문이나 매매 권유가 아닙니다. 회사는 시세 정보의 정확성·완전성을
보증하지 않으며, 이를 근거로 한 이용자의 판단과 그 결과에 대해 책임지지 않습니다.

제 7 조 (개인정보 보호)
회사는 관련 법령이 정하는 바에 따라 이용자의 개인정보를 보호합니다.
자세한 사항은 별도의 개인정보 처리방침에 따릅니다.

(이하 v1.0 출시 전 법무 검토 필수 — 현재 placeholder)`,
  },
  privacy: {
    title: "개인정보 처리방침",
    body: `PEEKA 개인정보 처리방침 (v1.0)

제 1 조 (수집하는 개인정보 항목)
회사는 회원 관리, 서비스 제공, 고객 상담을 위해 다음 정보를 수집합니다.

1. 소셜 로그인 시 수집 항목 (가입은 소셜 로그인으로만 가능합니다)
   - Google / Apple 계정 이메일, 프로필 이름
   - 소셜 서비스가 부여한 회원 식별자
2. 서비스 이용 중 수집 항목
   - 컬렉션·관심 카드·스캔 기록
   - 카드 인식 개선을 위해 이용자가 촬영한 카드 이미지와 인식 결과
   - 디바이스 정보 (OS, 기종, 앱 버전)

제 2 조 (개인정보의 이용 목적)
서비스 제공, 회원 관리, 카드 인식 정확도 개선, 서비스 개선, 마케팅 (선택 동의 시) 등에
활용됩니다.

제 3 조 (보유 및 이용 기간)
1. 회원 탈퇴 시 7일 이내 파기 (단, 관련 법령에 의해 보존 의무가 있는 경우 제외)
2. 회원 식별 정보: 회원 가입 ~ 탈퇴
3. 카드 이미지: 인식 모델 개선 목적의 보관 기간 경과 후 파기

제 4 조 (제3자 제공)
이용자가 사전에 동의한 경우와 법령에 의해 요구되는 경우를 제외하고 제공하지 않습니다.

제 5 조 (이용자의 권리)
이용자는 언제든지 본인의 개인정보를 조회, 수정, 삭제, 처리정지를 요청할 수 있습니다.

(이하 v1.0 출시 전 법무 검토 필수 — 현재 placeholder)`,
  },
  marketing: {
    title: "마케팅 정보 수신 동의",
    body: `PEEKA 마케팅 정보 수신 동의 (선택)

제 1 조 (목적)
마케팅 정보 수신에 동의하시면, 회사는 새 기능 출시, 이벤트, 시세 소식 등의 정보를
이메일 또는 푸시 알림으로 안내해드릴 수 있습니다.

제 2 조 (수신 방법)
- 이메일: 소셜 로그인 계정에 등록된 이메일 주소
- 푸시: 앱 알림 권한 허용 시

제 3 조 (동의 철회)
언제든지 내 정보 > 알림 설정에서 변경할 수 있습니다.
이메일 하단의 수신거부 링크로도 즉시 해지 가능합니다.

(이하 v1.0 출시 전 마케팅팀 검토 필수 — 현재 placeholder)`,
  },
  oss: {
    title: "오픈소스 라이선스",
    body: `PEEKA 앱이 사용 중인 주요 오픈소스 라이선스 목록입니다.

React (MIT License)
React Router (MIT License)
Zustand (MIT License)
Capacitor (MIT License)
Vite (MIT License)
TypeScript (Apache License 2.0)

전체 목록 및 상세 라이선스 텍스트는 빌드 산출물 내 NOTICE 파일에서 확인할 수 있습니다.

(이하 v1.0 출시 전 라이선스 자동 수집 스크립트로 교체 — 현재 placeholder)`,
  },
};
